import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { X, Plus, Trash2, Upload, FileText, ExternalLink, Loader2, HardHat, Save, Sparkles, Mail, Printer } from 'lucide-react';
import { IfAIEnabled } from '../contexts/AIVisibilityContext';
import { toast } from 'sonner';
import SaveSnapshotButton from './SaveSnapshotButton';
import { printHtml } from '../lib/print';
import DocumentHeader from './DocumentHeader';
import DocumentImportDialog from './DocumentImportDialog';
import DocumentViewerDialog from './DocumentViewerDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

/* Subcontractors — Hyatt-branded, Apple-style table of GC subcontracted
   vendors. Columns: Name · Contracts · Invoices · Scope of Work. Singleton
   doc on the backend; admin/engineer can mutate, viewers see read-only. */

function makeRow() {
  return { id: `tmp-${Math.random().toString(36).slice(2, 10)}`, name: '', contracts: [], invoices: [], scope_of_work: '' };
}

export default function SubcontractorsDialog({ open, onClose, hasFullAccess, adminToken }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [viewDoc, setViewDoc] = useState({ url: '', title: '' });
  const [uploadingFor, setUploadingFor] = useState(''); // `${rowId}-${kind}`
  // AI Progress Report state
  const [reportOpen, setReportOpen] = useState(false);
  const [reportBusy, setReportBusy] = useState(false);
  const [reportNotes, setReportNotes] = useState('');
  const [reportWeek, setReportWeek] = useState(() => new Date().toISOString().slice(0, 10));
  const [reportRecipient, setReportRecipient] = useState('Ownership');
  // Email — comma-separated list typed by GM; the saved address book in
  // /api/admin/email/recipients is merged automatically by the backend.
  const [reportEmails, setReportEmails] = useState('');
  const [emailBusy, setEmailBusy] = useState(false);
  // Custom document name — persisted via DocumentHeader.
  const [docName, setDocName] = useState('Subcontractor Register');
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    if (!adminToken) return;
    try {
      const { data } = await axios.get(`${API}/subcontractors`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setRows((data.rows || []).map((r) => ({
        id: r.id || `tmp-${Math.random().toString(36).slice(2, 10)}`,
        name: r.name || '',
        contracts: r.contracts || [],
        invoices: r.invoices || [],
        scope_of_work: r.scope_of_work || '',
      })));
      setLoaded(true);
      setDirty(false);
    } catch (e) {
      toast.error('Could not load subcontractors');
    }
  }, [adminToken]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const persist = async () => {
    if (!hasFullAccess) return;
    setSaving(true);
    try {
      const payload = { rows: rows.map((r) => ({
        id: r.id && !r.id.startsWith('tmp-') ? r.id : undefined,
        name: r.name,
        contracts: r.contracts,
        invoices: r.invoices,
        scope_of_work: r.scope_of_work,
      })) };
      const { data } = await axios.put(`${API}/admin/subcontractors`, payload, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setRows((data.rows || []).map((r) => ({
        id: r.id, name: r.name || '', contracts: r.contracts || [], invoices: r.invoices || [], scope_of_work: r.scope_of_work || '',
      })));
      setDirty(false);
      toast.success('Subcontractors saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addRow = () => { setRows((rs) => [...rs, makeRow()]); setDirty(true); };
  const removeRow = (rowId) => {
    if (!window.confirm('Remove this subcontractor row?')) return;
    setRows((rs) => rs.filter((r) => r.id !== rowId));
    setDirty(true);
  };
  const updateRow = (rowId, patch) => {
    setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, ...patch } : r)));
    setDirty(true);
  };

  const uploadFor = async (rowId, kind, file) => {
    if (!hasFullAccess || !file) return;
    setUploadingFor(`${rowId}-${kind}`);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('keep_raw', '1');   // keep PDFs as PDF (not auto-converted to image)
      const { data: up } = await axios.post(`${API}/admin/upload`, fd, {
        headers: { Authorization: `Bearer ${adminToken}`, 'Content-Type': 'multipart/form-data' },
      });
      const attachment = {
        url: up.url, label: file.name || '', kind: up.kind || up.ext || 'other',
        uploaded_at: new Date().toISOString(),
      };
      setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, [kind]: [...(r[kind] || []), attachment] } : r)));
      setDirty(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingFor('');
    }
  };

  const removeAttachment = (rowId, kind, idx) => {
    setRows((rs) => rs.map((r) => (r.id === rowId ? { ...r, [kind]: r[kind].filter((_, i) => i !== idx) } : r)));
    setDirty(true);
  };

  const generateReport = async () => {
    if (!hasFullAccess) return;
    if (rows.length === 0) { toast.error('Add at least one subcontractor first'); return; }
    if (dirty) { toast.error('Save your changes before generating a report'); return; }
    setReportBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/subcontractors/progress-report`,
        {
          week_ending: reportWeek,
          gm_notes: reportNotes.trim(),
          recipient: reportRecipient.trim() || 'Ownership',
        },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
          responseType: 'blob',
          timeout: 120000,
        },
      );
      // Trigger a browser download
      const blob = new Blob([data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `DRH_Subcontractor_Progress_${reportWeek || 'weekly'}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success('Progress report downloaded');
      setReportOpen(false);
    } catch (e) {
      // Blob responses don't expose `.data.detail` cleanly — try to read.
      let detail = 'Report generation failed';
      try {
        if (e?.response?.data instanceof Blob) {
          const txt = await e.response.data.text();
          try { detail = JSON.parse(txt).detail || detail; } catch { detail = txt.slice(0, 200) || detail; }
        } else {
          detail = e?.response?.data?.detail || detail;
        }
      } catch { /* keep default */ }
      toast.error(detail);
    } finally {
      setReportBusy(false);
    }
  };

  // Email the report — re-runs the AI step on the backend (always fresh
  // data) and ships the PDF as an attachment via Resend. Address book in
  // /api/admin/email/recipients is merged into whatever the GM typed in.
  const emailReport = async () => {
    if (!hasFullAccess) return;
    if (rows.length === 0) { toast.error('Add at least one subcontractor first'); return; }
    if (dirty) { toast.error('Save your changes before emailing the report'); return; }
    const explicit = (reportEmails || '')
      .split(/[,;\n]/)
      .map((s) => s.trim())
      .filter((s) => /.+@.+\..+/.test(s));
    setEmailBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/email/subcontractor-report`,
        {
          week_ending: reportWeek,
          gm_notes: reportNotes.trim(),
          recipient_label: reportRecipient.trim() || 'Ownership',
          recipients: explicit,
          use_default_recipients: true,
        },
        { headers: { Authorization: `Bearer ${adminToken}` }, timeout: 120000 },
      );
      toast.success(`Sent to ${(data.to || []).length} recipient${(data.to || []).length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Email send failed');
    } finally {
      setEmailBusy(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="subcontractors-dialog"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      style={{ backgroundColor: 'rgba(15,23,42,0.65)', backdropFilter: 'blur(8px)' }}
    >
      <div className="w-full max-w-6xl max-h-[92vh] flex flex-col rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: '#FAF8F2' }}>
        {/* Header — Hyatt blue ribbon */}
        <div className="flex items-center justify-between px-6 py-4" style={{ background: 'linear-gradient(135deg, #003DA5 0%, #1F4FBE 100%)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(255,255,255,0.18)' }}>
              <HardHat className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-white/80" style={{ fontFamily: 'Manrope' }}>General Contractor · TLDI</p>
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: 'Manrope' }}>Subcontractors</h2>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasFullAccess && (
              <IfAIEnabled>
                <button
                  data-testid="subcontractors-progress-report-btn"
                  onClick={() => setReportOpen((v) => !v)}
                  disabled={reportBusy}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold transition disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', color: 'white', fontFamily: 'Manrope' }}
                  title="Draft a one-page AI weekly status report"
                >
                  <Sparkles className="w-3.5 h-3.5" /> AI progress report
                </button>
              </IfAIEnabled>
            )}
            {hasFullAccess && (
              <button
                data-testid="subcontractors-save"
                onClick={persist}
                disabled={!dirty || saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white transition disabled:opacity-50"
                style={{ backgroundColor: dirty ? '#15803D' : 'rgba(255,255,255,0.18)', fontFamily: 'Manrope' }}
              >
                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                {saving ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
              </button>
            )}
            <button data-testid="subcontractors-close" onClick={onClose} type="button" aria-label="Close" className="w-11 h-11 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center hover:scale-110 active:scale-90 transition-transform duration-75 cursor-pointer" style={{ touchAction: 'manipulation' }}>
              <X className="w-5 h-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        {/* AI Progress Report — collapsible panel that drops below the
            header. Lets the GM tweak week-ending date, recipient and
            color notes before triggering the AI + PDF pipeline. */}
        {hasFullAccess && reportOpen && (
          <div
            data-testid="subcontractors-report-panel"
            className="px-6 py-4 border-b"
            style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-4 h-4" style={{ color: '#6366F1' }} />
              <p className="text-[11px] uppercase tracking-[0.15em] font-bold" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>
                AI Weekly Progress Report · GPT-5.2
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div>
                <label className="block text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>Week ending</label>
                <input
                  data-testid="report-week"
                  type="date"
                  value={reportWeek}
                  onChange={(e) => setReportWeek(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                  style={{ border: '1px solid #D6D0C0', backgroundColor: 'white', fontFamily: 'Manrope', color: '#1A2E2E' }}
                />
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>Prepared for</label>
                <input
                  data-testid="report-recipient"
                  value={reportRecipient}
                  onChange={(e) => setReportRecipient(e.target.value)}
                  placeholder="Ownership / Grupo Tortuga / Hyatt asset team…"
                  className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                  style={{ border: '1px solid #D6D0C0', backgroundColor: 'white', fontFamily: 'Manrope', color: '#1A2E2E' }}
                />
              </div>
            </div>
            <div className="mb-3">
              <label className="block text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>GM color notes (optional)</label>
              <textarea
                data-testid="report-notes"
                value={reportNotes}
                onChange={(e) => setReportNotes(e.target.value)}
                rows={3}
                placeholder="Any blockers, breakthroughs, or context for the AI to weave in. e.g. 'Acme Electrical delayed by customs; expect resume Mon.'"
                className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2 resize-none"
                style={{ border: '1px solid #D6D0C0', backgroundColor: 'white', fontFamily: 'Manrope', color: '#1A2E2E' }}
              />
            </div>
            <div className="mb-3">
              <label className="block text-[9px] uppercase tracking-wider font-bold mb-1" style={{ color: '#1F4FBE', fontFamily: 'Manrope' }}>Email recipients (optional · comma or newline separated)</label>
              <input
                data-testid="report-emails"
                value={reportEmails}
                onChange={(e) => setReportEmails(e.target.value)}
                placeholder="ceo@grupotortuga.com, asset.team@hyatt.com"
                className="w-full px-3 py-2 rounded-lg text-sm outline-none focus:ring-2"
                style={{ border: '1px solid #D6D0C0', backgroundColor: 'white', fontFamily: 'Manrope', color: '#1A2E2E' }}
              />
              <p className="text-[10px] mt-1" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                Saved address book (admin → Email Recipients) is merged in automatically.
              </p>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                type="button"
                data-testid="subcontractor-print"
                title="Print this subcontractor list"
                onClick={() => {
                  const esc = (v) => String(v ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
                  const body = `
                    <p class="kicker">DREAMS ROSE HALL · SUBCONTRACTORS</p>
                    <h1>${esc(docName || 'Subcontractor Register')}</h1>
                    <p class="meta">Week of ${esc(reportWeek || '—')} · ${rows.length} record${rows.length === 1 ? '' : 's'} · Printed ${esc(new Date().toLocaleString())}</p>
                    <table>
                      <thead><tr><th>#</th><th>Name</th><th>Scope of work</th><th>Contracts</th><th>Invoices</th></tr></thead>
                      <tbody>${rows.map((r, i) => `<tr><td>${i + 1}</td><td>${esc(r.name)}</td><td>${esc(r.scope_of_work)}</td><td>${(r.contracts || []).length}</td><td>${(r.invoices || []).length}</td></tr>`).join('')}</tbody>
                    </table>
                  `;
                  printHtml(body, docName || 'Subcontractor Register');
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white"
                style={{ backgroundColor: '#0A1A2F', fontFamily: 'Manrope' }}
              >
                <Printer className="w-3.5 h-3.5" /> Print
              </button>
              <SaveSnapshotButton
                kind="subcontractor"
                defaultTitle={docName || `Subcontractors · ${reportWeek || new Date().toLocaleDateString()}`}
                getPayload={() => ({ rows, report_week: reportWeek, doc_name: docName })}
                testid="subcontractor-save-snapshot"
              />
              <button
                type="button"
                data-testid="subcontractors-import-btn"
                onClick={() => setImportOpen(true)}
                title="Import a PDF / Word / Excel / CSV into this register"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold text-white"
                style={{ background: 'linear-gradient(135deg, #1F4FBE 0%, #1E40AF 100%)', fontFamily: 'Manrope' }}
              >
                <Upload className="w-3.5 h-3.5" /> Import file
              </button>
              <button
                data-testid="report-generate"
                onClick={generateReport}
                disabled={reportBusy || dirty || emailBusy}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(99,102,241,0.35)' }}
              >
                {reportBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {reportBusy ? 'Drafting (~10-20s)…' : 'Download PDF'}
              </button>
              <button
                data-testid="report-email"
                onClick={emailReport}
                disabled={emailBusy || reportBusy || dirty}
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold text-white transition hover:-translate-y-0.5 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #0EA5E9 0%, #1F4FBE 100%)', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(31,79,190,0.35)' }}
                title="Email the AI-drafted PDF directly to ownership via Resend"
              >
                {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                {emailBusy ? 'Sending…' : 'Email to ownership'}
              </button>
              {dirty && (
                <p className="text-[11px]" style={{ color: '#B45309', fontFamily: 'Manrope' }}>
                  Save your changes first.
                </p>
              )}
            </div>
          </div>
        )}

        {/* Body — table */}
        <div className="flex-1 overflow-auto p-6">
          {/* Branded centered banner + editable document name. */}
          {hasFullAccess && (
            <DocumentHeader
              scopeId="subcontractors"
              autoName="Subcontractor Register"
              onChange={setDocName}
              readOnly={!hasFullAccess}
            />
          )}
          {!loaded ? (
            <div className="flex items-center justify-center py-16 text-sm" style={{ color: '#5C6B6B' }}>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading subcontractors…
            </div>
          ) : (
            <div className="rounded-2xl overflow-hidden shadow-sm" style={{ backgroundColor: 'white', border: '1px solid #E2DBC8' }}>
              {/* Header row — Apple-style: tight, bold uppercase, Hyatt-blue accents */}
              <div className="grid grid-cols-12 gap-0 px-4 py-3 text-[10px] uppercase tracking-[0.15em] font-bold border-b" style={{ borderColor: '#E2DBC8', backgroundColor: '#F5F0E1', color: '#1F4FBE', fontFamily: 'Manrope' }}>
                <div className="col-span-3">Subcontractor</div>
                <div className="col-span-3">Contract</div>
                <div className="col-span-3">Invoices</div>
                <div className="col-span-3">Scope of Work</div>
              </div>

              {/* Rows */}
              {rows.length === 0 && (
                <div className="px-6 py-16 text-center text-sm" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
                  No subcontractors yet.
                  {hasFullAccess && <> Click <strong>+ Add subcontractor</strong> below to add the first one.</>}
                </div>
              )}
              {rows.map((row) => (
                <SubcontractorRow
                  key={row.id}
                  row={row}
                  hasFullAccess={hasFullAccess}
                  uploadingFor={uploadingFor}
                  onUpdate={(patch) => updateRow(row.id, patch)}
                  onRemove={() => removeRow(row.id)}
                  onUpload={(kind, file) => uploadFor(row.id, kind, file)}
                  onRemoveAttachment={(kind, idx) => removeAttachment(row.id, kind, idx)}
                  onView={(payload) => setViewDoc(payload)}
                />
              ))}
            </div>
          )}

          {/* Totals summary */}
          {loaded && rows.length > 0 && (() => {
            const totalContracts = rows.reduce((n, r) => n + (r.contracts || []).length, 0);
            const totalInvoices = rows.reduce((n, r) => n + (r.invoices || []).length, 0);
            const withScope = rows.filter((r) => (r.scope_of_work || '').trim()).length;
            return (
              <div
                data-testid="subcontractors-totals"
                className="mt-4 p-3 rounded-xl flex flex-wrap items-center gap-4 text-xs"
                style={{ backgroundColor: '#EAF2FF', border: '1px solid #C9D6E6', fontFamily: 'Manrope' }}
              >
                <span className="font-bold uppercase tracking-wider text-[10px]" style={{ color: '#003DA5' }}>Totals</span>
                <span style={{ color: '#1A2E2E' }}>Subcontractors: <strong>{rows.length}</strong></span>
                <span style={{ color: '#15803D' }}>Contracts on file: <strong>{totalContracts}</strong></span>
                <span style={{ color: '#1F4FBE' }}>Invoices on file: <strong>{totalInvoices}</strong></span>
                <span style={{ color: '#7C2D12' }}>With scope: <strong>{withScope}</strong></span>
              </div>
            );
          })()}

          {hasFullAccess && loaded && (
            <button
              data-testid="subcontractors-add-row"
              onClick={addRow}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-bold transition hover:-translate-y-0.5"
              style={{ backgroundColor: '#003DA5', color: 'white', fontFamily: 'Manrope', boxShadow: '0 4px 16px rgba(0,61,165,0.25)' }}
            >
              <Plus className="w-4 h-4" /> Add subcontractor
            </button>
          )}

          {!hasFullAccess && loaded && (
            <p className="mt-4 text-[11px] text-center" style={{ color: '#7A8C8C', fontFamily: 'Manrope' }}>
              Read-only view · Editing is reserved for admin and engineer roles.
            </p>
          )}
        </div>
      </div>

      {/* Universal document import — converts PDF / Word / Excel / CSV
          into subcontractor rows. */}
      <DocumentImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        targetType="subcontractors"
        activeListName="Subcontractor Register"
        newNameDefault="Imported subcontractors"
        onCommit={async (importedRows, { mode }) => {
          const merged = mode === 'merge' ? [...rows, ...importedRows.map(makeRow).map((r, i) => ({ ...r, ...importedRows[i] }))] : importedRows.map(makeRow).map((r, i) => ({ ...r, ...importedRows[i] }));
          setRows(merged); setDirty(true);
          // Auto-persist so the import isn't lost if the user closes the dialog.
          await axios.put(`${API}/admin/subcontractors`, { rows: merged }, { headers: { Authorization: `Bearer ${adminToken}` } });
          await load();
        }}
      />

      {/* In-app document viewer — see DocumentViewerDialog comment. */}
      <DocumentViewerDialog
        open={!!viewDoc.url}
        url={viewDoc.url}
        title={viewDoc.title}
        onClose={() => setViewDoc({ url: '', title: '' })}
      />
    </div>
  );
}

function SubcontractorRow({ row, hasFullAccess, uploadingFor, onUpdate, onRemove, onUpload, onRemoveAttachment, onView }) {
  const contractRef = useRef(null);
  const invoiceRef = useRef(null);
  const ctrUploading = uploadingFor === `${row.id}-contracts`;
  const invUploading = uploadingFor === `${row.id}-invoices`;

  return (
    <div data-testid={`subcontractor-row-${row.id}`} className="grid grid-cols-12 gap-0 px-4 py-4 border-b last:border-b-0" style={{ borderColor: '#F1ECDC', fontFamily: 'Manrope' }}>
      {/* Name */}
      <div className="col-span-3 pr-3">
        {hasFullAccess ? (
          <input
            data-testid={`subcontractor-name-${row.id}`}
            value={row.name}
            onChange={(e) => onUpdate({ name: e.target.value })}
            placeholder="Vendor name"
            className="w-full px-3 py-2 rounded-lg text-sm font-semibold outline-none focus:ring-2"
            style={{ border: '1px solid #E2DBC8', backgroundColor: '#FAF8F2', color: '#1A2E2E' }}
          />
        ) : (
          <p className="px-1 py-2 text-sm font-semibold" style={{ color: '#1A2E2E' }}>{row.name || '—'}</p>
        )}
        {hasFullAccess && (
          <button
            data-testid={`subcontractor-remove-${row.id}`}
            onClick={onRemove}
            className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold transition hover:underline"
            style={{ color: '#DC2626' }}
            title="Remove this subcontractor row"
          >
            <Trash2 className="w-3 h-3" /> Remove
          </button>
        )}
      </div>

      {/* Contract files */}
      <div className="col-span-3 pr-3">
        <AttachmentList items={row.contracts} hasFullAccess={hasFullAccess} onRemove={(idx) => onRemoveAttachment('contracts', idx)} onView={onView} testIdPrefix={`subcontractor-contract-${row.id}`} />
        {hasFullAccess && (
          <>
            <button
              data-testid={`subcontractor-add-contract-${row.id}`}
              onClick={() => contractRef.current?.click()}
              disabled={ctrUploading}
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition disabled:opacity-50"
              style={{ backgroundColor: '#E7F0FF', color: '#1F4FBE' }}
            >
              {ctrUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {ctrUploading ? 'Uploading…' : '+ Contract file'}
            </button>
            <input
              ref={contractRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp,.dwg,.dxf"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload('contracts', f); e.target.value = ''; }}
            />
          </>
        )}
      </div>

      {/* Invoices */}
      <div className="col-span-3 pr-3">
        <AttachmentList items={row.invoices} hasFullAccess={hasFullAccess} onRemove={(idx) => onRemoveAttachment('invoices', idx)} onView={onView} testIdPrefix={`subcontractor-invoice-${row.id}`} />
        {hasFullAccess && (
          <>
            <button
              data-testid={`subcontractor-add-invoice-${row.id}`}
              onClick={() => invoiceRef.current?.click()}
              disabled={invUploading}
              className="mt-2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold transition disabled:opacity-50"
              style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
            >
              {invUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {invUploading ? 'Uploading…' : '+ Invoice file'}
            </button>
            <input
              ref={invoiceRef}
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload('invoices', f); e.target.value = ''; }}
            />
          </>
        )}
      </div>

      {/* Scope of work */}
      <div className="col-span-3 pl-1">
        {hasFullAccess ? (
          <textarea
            data-testid={`subcontractor-scope-${row.id}`}
            value={row.scope_of_work}
            onChange={(e) => onUpdate({ scope_of_work: e.target.value })}
            placeholder="Describe the scope of work…"
            rows={4}
            className="w-full px-3 py-2 rounded-lg text-xs outline-none focus:ring-2 resize-none"
            style={{ border: '1px solid #E2DBC8', backgroundColor: '#FAF8F2', color: '#1A2E2E' }}
          />
        ) : (
          <p className="px-1 py-2 text-xs whitespace-pre-wrap" style={{ color: '#4A5D5D' }}>{row.scope_of_work || '—'}</p>
        )}
      </div>
    </div>
  );
}

function AttachmentList({ items = [], hasFullAccess, onRemove, onView, testIdPrefix }) {
  if (!items.length) return <p className="text-[11px] italic" style={{ color: '#9CA3AF' }}>No files yet</p>;
  return (
    <ul className="flex flex-col gap-1">
      {items.map((it, i) => {
        const href = it.url?.startsWith('http') ? it.url : `${BACKEND}${it.url}`;
        const label = it.label || `File ${i + 1}`;
        return (
          <li key={`${it.url}-${i}`} className="flex items-center gap-1.5">
            <FileText className="w-3 h-3 flex-shrink-0" style={{ color: '#1F4FBE' }} />
            <button
              type="button"
              data-testid={`${testIdPrefix}-link-${i}`}
              onClick={() => onView?.({ url: href, title: label })}
              className="flex-1 text-left text-[11px] font-semibold truncate hover:underline cursor-pointer"
              style={{ color: '#1A2E2E', background: 'transparent', border: 0, padding: 0 }}
              title={it.label || it.url}
            >
              {label}
            </button>
            <a href={href} target="_blank" rel="noreferrer" className="opacity-60 hover:opacity-100" title="Open in a new tab">
              <ExternalLink className="w-3 h-3" style={{ color: '#1F4FBE' }} />
            </a>
            {hasFullAccess && (
              <button
                data-testid={`${testIdPrefix}-remove-${i}`}
                onClick={() => onRemove(i)}
                className="opacity-60 hover:opacity-100"
                title="Remove file"
              >
                <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
